import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { FaArrowLeft, FaEye, FaEyeSlash } from 'react-icons/fa';
import {
  Container,
  Header,
  HeaderLeft,
  BackButton,
  Title,
  Content,
  HeaderButton,
  ControlsContainer,
} from '../common/styles';
import { MainContainer } from './styles';
import { useAlert } from '../../contexts/AlertContext';
import { useAbility } from '../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../constants/permissions';
import { feedbackApi } from '../../services/api';
import ResultsCount from '../common/ResultsCount';
import AssignmentGrid from './components/AssignmentGrid';
import AssignmentFilters from './components/AssignmentFilters';
import ConfirmationModal from '../common/ConfirmationModal';
import useIndividualAssignmentsStore from '../../store/individualAssignmentsStore';
import useGridNavigation from '../../hooks/useGridNavigation';
import {
  FEEDBACK_SESSION_VIEW_MODES
} from '../../constants/feedbackSessionViewModes';

const IndividualAssignments = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const ability = useAbility();
  const gridRef = useRef(null);

  // Zustand store
  const {
    isLoading,
    isLoadingMore,
    pageInfo,
    selectedStatuses,
    selectedReviewTypes,
    selectedEmployee,
    showControls,
    fetchAssignmentsWithReset,
    fetchAssignmentsLoadMore,
    toggleStatus,
    toggleReviewType,
    setSelectedEmployee,
    setShowControls,
    setIsReturningFromDetails,
    isReturningFromDetails,
    getFilteredAssignments,
    removeAssignment,
    resetFilters,
  } = useIndividualAssignmentsStore();

  // Modal states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [assignmentToPublish, setAssignmentToPublish] = useState(null);

  // Grid navigation hook
  const {
    handleItemClick,
    handleGridScroll,
    showLoadingSpinner,
    shouldHideContent,
    loadMoreData,
    shouldLoadData,
  } = useGridNavigation({
    gridRef,
    items: getFilteredAssignments(),
    columnCount: 3,
    rowHeight: 620,
    routes: {
      list: '/individual-assignments',
      detail: () => `/individual-assignments/view`,
      new: '/feedback-sessions/new-individual',
      additionalRoutes: [
        { path: '/view', shouldLoadData: false },
        { path: '/edit-reviewers', shouldLoadData: false },
      ]
    },
    onReturnFromDetail: () => {
      setIsReturningFromDetails(true);
    },
    // Note: Not using onNavigateAway here because Individual Assignments navigates to
    // /feedback-sessions/new-individual which has a different path prefix, causing
    // the hook to incorrectly detect it as "navigating away". Instead, we manually
    // reset filters in handleBack when navigating to home.
    loadMoreDataCallback: fetchAssignmentsLoadMore,
    isLoading,
    isLoadingMore,
    pageInfo,
  });

  // Fetch assignments on mount
  useEffect(() => {
    if (shouldLoadData() && !isReturningFromDetails) {
      fetchAssignmentsWithReset();
    }
    if (isReturningFromDetails) {
      setIsReturningFromDetails(false);
    }
  }, [shouldLoadData, isReturningFromDetails, fetchAssignmentsWithReset, setIsReturningFromDetails]);

  // Helper to capture scroll state before navigation
  const captureScrollState = useCallback((assignment) => {
    const gridElement = gridRef.current?._outerRef;
    if (!gridElement) {
      return null;
    }

    const currentScrollPosition = gridElement.scrollTop;
    const items = getFilteredAssignments();
    const itemIndex = items.findIndex(item => item.id === assignment.id);
    const rowIndex = itemIndex !== -1 ? Math.floor(itemIndex / 3) : -1;
    const itemTopPosition = rowIndex * 620;
    const offsetFromTop = itemTopPosition - currentScrollPosition;

    return {
      scrollPosition: currentScrollPosition,
      itemId: assignment.id,
      itemIndex,
      rowIndex,
      offsetFromTop,
      timestamp: Date.now()
    };
  }, [getFilteredAssignments]);

  // Transform the individual assignment API response to sessionData format
  const transformAssignmentToSessionData = (assignment) => {
    const assignmentDate = new Date(assignment.assignedAt || Date.now());

    // Prefer sessionTemplate from reviewProcess, fall back to skillsMatrix
    const sessionTemplate = assignment.reviewProcess?.sessionTemplate;

    // Get reviewType from sessionTemplate first, then from reviewers
    const reviewType = sessionTemplate?.reviewType || assignment.reviewers?.[0]?.reviewType;

    // Transform employee data - handle grade and position arrays
    const transformedEmployee = {
      ...assignment.employee,
      orgGrades: assignment.employee.grade?.[0] || null,
      orgPositions: assignment.employee.position?.[0] || null
    };

    // Transform reviewers data
    const transformedReviewers = assignment.reviewers?.map(reviewer => ({
      reviewer: {
        id: reviewer.employee.id,
        fullName: reviewer.employee.fullName,
        azureId: reviewer.employee.azureId,
        assignment: {
          assignedAt: reviewer.assignedAt,
          completedAt: reviewer.completedAt,
          startedAt: reviewer.startedAt,
          order: reviewer.order || 1
        }
      },
      template: reviewer.template,
      reviewType: reviewer.reviewType
    })) || [];

    const sessionData = {
      id: assignment.id,
      reviewType: reviewType,
      sessionTemplate: sessionTemplate,
      deadline: assignment.reviewProcess?.deadline,
      sessionInfo: {
        id: assignment.id,
        name: `Individual Assignment - ${assignment.employee.fullName}`,
        month: assignmentDate.getMonth() + 1,
        year: assignmentDate.getFullYear(),
        deadline: assignment.reviewProcess?.deadline,
        allowEmployeeToAssignReviewers: assignment.reviewProcess?.allowEmployeeToAssignReviewers || false
      },
      employeeAssignments: [{
        employeeId: assignment.employee.id,
        employee: transformedEmployee,
        assignment: {
          id: assignment.id,
          assignedAt: assignment.assignedAt,
          completedAt: assignment.completedAt,
        },
        matrix: assignment.skillsMatrix,
        reviewers: transformedReviewers
      }]
    };

    return sessionData;
  };

  // Assignment action handlers
  const handleAssignment = useCallback(async (assignment, viewMode, route) => {
    try {
      const scrollState = captureScrollState(assignment);
      const assignmentData = await feedbackApi.getIndividualAssignmentById(assignment.id);

      // Transform the API response to match the sessionData structure expected by ScheduleSession
      const sessionData = transformAssignmentToSessionData(assignmentData);

      navigate(route, {
        state: {
          sessionData,
          viewMode,
          fromList: true,
          scrollState,
          returnPath: '/individual-assignments'
        }
      });
    } catch (error) {
      showAlert(error.message || 'Failed to fetch assignment details', 'error', error);
    }
  }, [navigate, showAlert, captureScrollState]);

  const handleViewDetails = useCallback((assignment) => {
    if (!ability.can(ACTIONS.READ, SUBJECTS.INDIVIDUAL_ASSIGNMENT)) {
      showAlert('You do not have permission to view assignment details', 'error');
      return;
    }
    handleAssignment(assignment, FEEDBACK_SESSION_VIEW_MODES.VIEW, '/feedback-sessions/new-individual');
  }, [ability, handleAssignment, showAlert]);

  const handleEditReviewers = useCallback((assignment) => {
    if (!ability.can(ACTIONS.UPDATE, SUBJECTS.INDIVIDUAL_ASSIGNMENT)) {
      showAlert('You do not have permission to edit assignment reviewers', 'error');
      return;
    }
    handleAssignment(assignment, FEEDBACK_SESSION_VIEW_MODES.EDIT_REVIEWERS, '/feedback-sessions/new-individual');
  }, [ability, handleAssignment, showAlert]);

  const handleViewReport = useCallback((assignment) => {
    if (!ability.can(ACTIONS.READ, SUBJECTS.INDIVIDUAL_ASSIGNMENT)) {
      showAlert('You do not have permission to view assignment reports', 'error');
      return;
    }
    const scrollState = captureScrollState(assignment);
    navigate(`/feedback-sessions/individual-report/${assignment.id}`, {
      state: {
        fromList: true,
        scrollState
      }
    });
  }, [ability, navigate, showAlert, captureScrollState]);

  const handleDeleteClick = useCallback((assignment) => {
    if (!ability.can(ACTIONS.DELETE, SUBJECTS.INDIVIDUAL_ASSIGNMENT)) {
      showAlert('You do not have permission to delete assignments', 'error');
      return;
    }
    setAssignmentToDelete(assignment);
    setDeleteModalOpen(true);
  }, [ability, showAlert]);

  const handleDeleteAssignment = useCallback(async () => {
    if (!assignmentToDelete) {
      return;
    }

    if (!ability.can(ACTIONS.DELETE, SUBJECTS.INDIVIDUAL_ASSIGNMENT)) {
      showAlert('You do not have permission to delete assignments', 'error');
      setDeleteModalOpen(false);
      setAssignmentToDelete(null);
      return;
    }

    try {
      await feedbackApi.deleteIndividualAssignment(assignmentToDelete.id);
      removeAssignment(assignmentToDelete.id);
      showAlert('Individual assignment deleted successfully', 'success');
    } catch (error) {
      showAlert(error.message || 'Failed to delete individual assignment', 'error', error);
    } finally {
      setDeleteModalOpen(false);
      setAssignmentToDelete(null);
    }
  }, [assignmentToDelete, ability, removeAssignment, showAlert]);

  const handlePublishToInProgressClick = useCallback((assignment) => {
    if (!ability.can(ACTIONS.PUBLISH, SUBJECTS.INDIVIDUAL_ASSIGNMENT)) {
      showAlert('You do not have permission to publish assignments', 'error');
      return;
    }
    setAssignmentToPublish(assignment);
    setPublishModalOpen(true);
  }, [ability, showAlert]);

  const handlePublishToInProgress = useCallback(async () => {
    if (!assignmentToPublish) {
      return;
    }

    if (!ability.can(ACTIONS.PUBLISH, SUBJECTS.INDIVIDUAL_ASSIGNMENT)) {
      showAlert('You do not have permission to publish assignments', 'error');
      setPublishModalOpen(false);
      setAssignmentToPublish(null);
      return;
    }

    try {
      await feedbackApi.publishIndividualAssignmentToInProgress(assignmentToPublish.id);
      // Refresh assignments data from server to ensure consistency
      await fetchAssignmentsWithReset();
      showAlert('Individual assignment published to in progress successfully', 'success');
    } catch (error) {
      showAlert(error.message || 'Failed to publish individual assignment', 'error');
    } finally {
      setPublishModalOpen(false);
      setAssignmentToPublish(null);
    }
  }, [assignmentToPublish, ability, fetchAssignmentsWithReset, showAlert]);

  const handleScheduleIndividual = useCallback(() => {
    navigate('/feedback-sessions/new-individual', {
      state: { returnPath: '/individual-assignments' }
    });
  }, [navigate]);

  const handleBack = useCallback(() => {
    // Reset filters when navigating away from the section
    resetFilters();
    navigate('/');
  }, [navigate, resetFilters]);

  const filteredAssignments = getFilteredAssignments();
  const hasFiltersApplied = selectedStatuses.length > 0 || selectedReviewTypes.length > 0 || selectedEmployee !== null;

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={handleBack}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>Individual Assignments</Title>
        </HeaderLeft>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <HeaderButton
            onClick={() => setShowControls(!showControls)}
            sx={{
              color: '#475569',
              borderColor: '#E2E8F0',
              '&:hover': {
                borderColor: '#CBD5E1',
                backgroundColor: '#F8FAFC',
              }
            }}
          >
            {showControls ? <FaEyeSlash /> : <FaEye />}
            {showControls ? 'Hide Filters' : 'Show Filters'}
          </HeaderButton>
        </Box>
      </Header>

      <Content>
        <ControlsContainer $show={showControls}>
          <AssignmentFilters
            selectedStatuses={selectedStatuses}
            onStatusToggle={toggleStatus}
            selectedReviewTypes={selectedReviewTypes}
            onReviewTypeToggle={toggleReviewType}
            selectedEmployee={selectedEmployee}
            onEmployeeChange={setSelectedEmployee}
            onNewAssignment={handleScheduleIndividual}
            ability={ability}
          />
        </ControlsContainer>

        <MainContainer>
          <ResultsCount count={filteredAssignments.length} singular="assignment" plural="assignments" />

          <AssignmentGrid
            assignments={filteredAssignments}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            pageInfo={{ ...pageInfo, totalCount: filteredAssignments.length }}
            handleItemClick={handleItemClick}
            handleGridScroll={handleGridScroll}
            showLoadingSpinner={showLoadingSpinner}
            shouldHideContent={shouldHideContent}
            loadMoreData={loadMoreData}
            gridRef={gridRef}
            onView={handleViewDetails}
            onViewReport={handleViewReport}
            onEditReviewers={handleEditReviewers}
            onDelete={handleDeleteClick}
            onPublish={handlePublishToInProgressClick}
            ability={ability}
            hasFiltersApplied={hasFiltersApplied}
          />
        </MainContainer>
      </Content>

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setAssignmentToDelete(null);
        }}
        onConfirm={handleDeleteAssignment}
        title="Delete Individual Assignment"
        message={`Are you sure you want to delete the individual assignment for "${assignmentToDelete?.employee?.fullName}"? This action cannot be undone.`}
        actionButtonText="Delete"
        actionButtonColor="#EF4444"
      />

      <ConfirmationModal
        isOpen={publishModalOpen}
        onClose={() => {
          setPublishModalOpen(false);
          setAssignmentToPublish(null);
        }}
        onConfirm={handlePublishToInProgress}
        title="Publish to In Progress"
        message={`Are you sure you want to publish the individual assignment for "${assignmentToPublish?.employee?.fullName}" to in progress? This will make the assignment available to the assigned individual and reviewers.`}
        actionButtonText="Publish"
        actionButtonColor="#0284C7"
      />
    </Container>
  );
};

export default IndividualAssignments;
